export default function FeatureSection() {
  const features = [
    {
      icon: 'ri-rocket-line',
      title: '70% Faster Sourcing',
      metric: 'vs Manual',
      desc: 'Skip the endless scrolling through 1688 and Alibaba. Our structured AllChinaBuySpreadsheet gives you pre-vetted products in seconds — drastically cutting research time so you can focus on scaling your store.',
      bgImage: 'https://readdy.ai/api/search-image?query=Clean%20modern%20workspace%20with%20a%20laptop%20on%20a%20warm%20wooden%20desk%20next%20to%20a%20small%20potted%20plant%2C%20soft%20natural%20light%20streaming%20through%20a%20large%20window%2C%20calm%20and%20focused%20atmosphere%2C%20no%20text%20visible%20on%20screen%2C%20no%20logos%2C%20editorial%20interior%20photography%20with%20warm%20scandinavian%20aesthetic&width=800&height=600&seq=feat-speed-v2&orientation=landscape',
    },
    {
      icon: 'ri-funds-box-line',
      title: 'Higher Profit Margins',
      metric: 'vs Agents',
      desc: 'Working with sourcing agents eats into your margins. The AllChinaBuySpreadsheet gives you direct access to supplier data without middleman markups — meaning you keep more of every sale and can price more competitively.',
      bgImage: 'https://readdy.ai/api/search-image?query=Neatly%20stacked%20kraft%20paper%20shipping%20boxes%20tied%20with%20natural%20twine%20on%20a%20warm%20wooden%20table%2C%20soft%20afternoon%20sunlight%2C%20clean%20ecommerce%20packaging%20scene%2C%20no%20logos%2C%20no%20text%2C%20commercial%20photography%20in%20warm%20earthy%20tones&width=800&height=600&seq=feat-profit-v2&orientation=landscape',
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Structured Discovery',
      metric: 'vs Random Browsing',
      desc: 'Instead of randomly browsing supplier platforms and hoping to stumble on winners, our curated spreadsheet organizes products by category with pricing, trends, and supplier ratings — turning chaos into a clear, repeatable system.',
      bgImage: 'https://readdy.ai/api/search-image?query=Organized%20flat%20lay%20of%20product%20samples%20including%20fabric%20swatches%2C%20a%20measuring%20tape%2C%20and%20a%20notebook%20with%20handwritten%20notes%20on%20a%20warm%20linen%20surface%2C%20soft%20natural%20overhead%20light%2C%20creative%20workspace%20aesthetic%2C%20no%20logos%2C%20no%20text%2C%20commercial%20photography%20in%20warm%20neutral%20tones&width=800&height=600&seq=feat-struct-v2&orientation=landscape',
    },
  ];

  const comparisonRows = [
    { label: 'Speed', sheet: 'Fast', manual: 'Slow', agents: 'Medium' },
    { label: 'Cost Efficiency', sheet: 'High', manual: 'Medium', agents: 'Low' },
    { label: 'Product Discovery', sheet: 'Structured', manual: 'Random', agents: 'Limited' },
    { label: 'Scalability', sheet: 'High', manual: 'Low', agents: 'Medium' },
    { label: 'Beginner Friendly', sheet: 'Yes', manual: 'No', agents: 'Medium' },
  ];

  return (
    <section className="relative py-20 md:py-28 px-6 md:px-10 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-left mb-16">
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            Why Sellers Win With
            <br />
            Our <span className="text-primary-500">Spreadsheet</span>
          </h2>
          <p className="text-base text-foreground-600 max-w-2xl leading-relaxed">
            The AllChinaBuySpreadsheet approach beats traditional sourcing methods in every way that matters.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {features.map((feat, idx) => (
            <div
              key={idx}
              className="group relative rounded-xl overflow-hidden border border-background-200/70 transition-all duration-300 hover:border-accent-200"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={feat.bgImage}
                  alt={feat.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100/90">
                      <i className={`${feat.icon} text-accent-700 text-sm`}></i>
                    </span>
                    <span className="text-xs font-semibold text-background-50/80 bg-background-50/15 px-2.5 py-1 rounded-full whitespace-nowrap">
                      {feat.metric}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-2">{feat.title}</h3>
                  <p className="text-sm text-background-50/80 leading-relaxed">{feat.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-background-100 rounded-2xl p-6 md:p-10 border border-background-200/70 overflow-x-auto">
          <h3 className="font-heading text-2xl font-bold text-foreground-950 mb-6">Feature Comparison</h3>
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-background-200/70">
                <th className="py-4 pr-6 text-sm font-semibold text-foreground-700 whitespace-nowrap">Feature</th>
                <th className="py-4 px-6 text-sm font-semibold text-primary-500 bg-primary-50/50 rounded-t-lg whitespace-nowrap">
                  AllChinaBuySpreadsheet
                </th>
                <th className="py-4 px-6 text-sm font-semibold text-foreground-600 whitespace-nowrap">Manual Sourcing</th>
                <th className="py-4 px-6 text-sm font-semibold text-foreground-600 whitespace-nowrap">Agents</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row) => (
                <tr key={row.label} className="border-b border-background-200/50">
                  <td className="py-4 pr-6 text-sm font-medium text-foreground-800 whitespace-nowrap">{row.label}</td>
                  <td className="py-4 px-6 text-sm font-semibold text-accent-700 bg-primary-50/30 whitespace-nowrap">{row.sheet}</td>
                  <td className="py-4 px-6 text-sm text-foreground-600 whitespace-nowrap">{row.manual}</td>
                  <td className="py-4 px-6 text-sm text-foreground-600 whitespace-nowrap">{row.agents}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}